import type { BriefInsurance } from "./PreVisitBrief";
import { CATEGORY_LABELS, SUMMARY_REASONS } from "./coverageModel";

// The brief's Coverage block, rendered from the structured facts the
// backend stores: one headline and only the exceptions a clinician or the
// front desk would act on before the visit. All wording lives here.

export interface BriefInsuranceLine {
  key: string;
  label: string;
  text: string;
  tone: "ok" | "warn" | "alert" | "muted";
}

export interface BriefInsuranceView {
  headline: string;
  lines: BriefInsuranceLine[];
  source: string;
}

const money = (v: number): string => `$${Math.round(v).toLocaleString("en-US")}`;

const mmdd = (iso?: string | null): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
  return m ? `${m[2]}/${m[3]}` : "";
};

const RELATIONSHIP_WORD: Record<string, string> = {
  spouse: "spouse",
  child: "child",
  parent: "parent",
  partner: "partner",
  other: "dependent",
  injured: "injured party",
};

const DRAWING: Array<[string, string]> = [
  ["lab", "labs"],
  ["allergy", "allergy testing"],
  ["pulmonary", "pulmonary tests"],
];

export const briefInsuranceView = (b: BriefInsurance): BriefInsuranceView => {
  const source =
    b.source === "stedi_271_stale"
      ? "last payer answer; tonight's check failed"
      : b.source === "stedi_271" || b.source === "stedi_271_cached"
        ? "verified with the payer"
        : "";
  const payer = b.payer_name || "The plan on file";
  const planLabel =
    b.plan_type && !payer.toLowerCase().includes(String(b.plan_type).toLowerCase())
      ? `${payer} · ${b.plan_type}`
      : payer;
  const lines: BriefInsuranceLine[] = [];

  if (b.state === "no_insurance") return { headline: "No insurance on file from the EHR", lines, source };
  if (b.state === "self_pay") return { headline: "Self-pay · no coverage lookup", lines, source };

  if (b.state === "inactive") {
    const end = mmdd(b.plan_end);
    let head: string;
    if (b.plan_status === "inactive") head = `${payer} plan terminated${end ? ` ${end}` : ""}.`;
    else if (b.ehr_status === "terminated" || b.ehr_status === "expired") {
      const ehrEnd = mmdd(b.ehr_end_date);
      head = `${payer} policy is ${b.ehr_status} in the EHR${ehrEnd ? ` (ended ${ehrEnd})` : ""}${end ? `; the payer reports coverage through ${end}.` : "."}`;
    } else
      head = `${payer} plan is marked inactive in the EHR${end ? `; the payer reports coverage through ${end}.` : "."}`;
    head += (b.other_coverage ?? []).length
      ? ` Other coverage on file: ${(b.other_coverage ?? []).join(", ")}.`
      : " No other coverage found.";
    head += " Front desk should confirm coverage before the visit.";
    return { headline: head, lines, source };
  }

  if (b.dependent) {
    const who = RELATIONSHIP_WORD[b.dependent.relationship] ?? "dependent";
    const holder = b.dependent.insured_name ? ` (${b.dependent.insured_name})` : "";
    lines.push({
      key: "dependent",
      label: "Subscriber",
      text: `Patient is covered as ${who} of the subscriber${holder}. Front desk may need the subscriber's date of birth.`,
      tone: "muted",
    });
  }

  if (b.state === "unverified") {
    let head = `${planLabel} · not verified overnight`;
    if (b.ehr_eligibility) head += ` · EHR eligibility: ${b.ehr_eligibility.toLowerCase()}`;
    if (b.unverified_reason) {
      lines.unshift({
        key: "unverified",
        label: "Why",
        text: SUMMARY_REASONS[b.unverified_reason] ?? b.unverified_reason,
        tone: "muted",
      });
    }
    return { headline: head, lines, source };
  }

  const ded = b.deductible;
  if (ded && ded.total > 0 && ded.met < ded.total) {
    let text = `${money(ded.met)} of ${money(ded.total)} met`;
    const drawing = DRAWING.filter(([k]) => b.categories?.[k]?.covered && b.categories?.[k]?.deductible_applies);
    if (drawing.length) {
      const pct = b.categories?.[drawing[0][0]]?.coinsurance_pct;
      const share = pct != null ? `${pct}% after the deductible` : "after the deductible";
      text += ` · ${drawing
        .slice(0, 2)
        .map(([, w]) => w)
        .join(" and ")} pay ${share}, so expect patient responsibility today`;
    }
    lines.unshift({ key: "deductible", label: "Deductible", text, tone: "warn" });
  }
  for (const [cat, flags] of Object.entries(b.categories ?? {})) {
    if (flags?.auth_required) {
      lines.push({
        key: `auth-${cat}`,
        label: CATEGORY_LABELS[cat] ?? cat,
        text: "Prior authorization required (payer indicator)",
        tone: "warn",
      });
    }
  }
  for (const r of b.remarks ?? []) {
    lines.push({
      key: `remark-${r.category}-${r.text}`,
      label: CATEGORY_LABELS[r.category] ?? r.category,
      text: r.text,
      tone: "muted",
    });
  }
  for (const r of b.plan_remarks ?? []) {
    lines.push({ key: `plan-${r}`, label: "Plan", text: r, tone: "muted" });
  }
  return { headline: planLabel, lines, source };
};
